<template>
    <!-- A4G Search API Redirect Handler - No visual output -->
    <div />
</template>

<script lang="ts">
import { createWidgetMixin } from 'vue-instantsearch/vue3/es'
import { defineComponent } from 'vue'
import { useLocalePath, useInternationalization, useNuxtApp, navigateTo } from '#imports'
import type { SearchResults } from 'algoliasearch-helper'
import { findSearchApiRedirect } from '~/utils/searchApiRedirect'

/**
 * A4G Search API Redirect Component (SEARCH-14)
 *
 * Handles redirects maintained per language on the backend and returned in the
 * `multi-search` response's `userData` — the one channel @meilisearch/instant-meilisearch
 * itself drops (see patches/@meilisearch+instant-meilisearch+0.31.3.patch), so this reads
 * it off algoliasearch-helper's SearchResults, which copies every key of the raw
 * per-index response (including the patched-through `userData`) onto itself.
 *
 * The connector only reports the URL into widget state; the redirect itself runs from the
 * component, because inject()-based composables (useLocalePath/useInternationalization)
 * resolve only while Vue has a currentInstance — which it does in setup(), but not inside
 * the connector's render(), which InstantSearch calls standalone (same constraint as
 * documented in app/directives/v-interpolate.ts).
 *
 * The parent holds its skeleton while the navigation is in flight (see SearchMeili.vue's
 * `isRedirecting`); `redirect-failed` releases that hold.
 */

interface RedirectState {
    url: string | null
}

const widgetMixin = createWidgetMixin({
    connector: (
        renderFn: (state: RedirectState, isFirstRender: boolean) => void,
        _unmountFn: () => void,
    ) => () => ({
        dispose() {},
        render({ results }: { results: SearchResults | null }) {
            renderFn({ url: findSearchApiRedirect(results)?.url ?? null }, false)
        },
    }),
})

export default defineComponent({
    mixins: [widgetMixin],
    emits: ['redirect-failed'],
    setup(_props, { emit }) {
        const nuxtApp = useNuxtApp()
        const localePath = useLocalePath()
        const { formatLink } = useInternationalization(localePath)

        function reportFailure(error: unknown) {
            console.error('[SearchQueryRedirectMeili] Failed to perform redirect:', error)
            emit('redirect-failed')
        }

        function performRedirect(url: string) {
            try {
                const isExternalUrl = /^https?:\/\//i.test(url)
                const target = isExternalUrl
                    ? url
                    : formatLink(url.startsWith('/') ? url : `/${url}`)

                // A watcher callback carries no Nuxt app context, which navigateTo requires.
                const navigation = nuxtApp.runWithContext(() => navigateTo(target, { external: isExternalUrl }))

                // vue-router reports a refused navigation as a resolved NavigationFailure
                // rather than a throw, so both outcomes have to be inspected.
                Promise.resolve(navigation)
                    .then((result) => {
                        if (result && typeof result === 'object' && 'type' in result) reportFailure(result)
                    })
                    .catch(reportFailure)
            } catch (error) {
                reportFailure(error)
            }
        }

        return { performRedirect }
    },
    computed: {
        url(): string | null {
            return this.state?.url ?? null
        },
    },
    watch: {
        url: {
            immediate: true,
            handler(url: string | null) {
                if (url) this.performRedirect(url)
            },
        },
    },
})
</script>
